(function (algolia) {
  "use strict";
  const render = algolia.externals.render;
  const html = algolia.externals.html;

  const algoliasearch = algolia.externals.algoliasearch;
  algolia.searchClient = algoliasearch(
    algolia.config.app_id,
    algolia.config.search_api_key
  );

  algolia.render = (template, container, data) => {
    return render(template(html, data), container);
  };

  const formatPrice = function formatPrice(value, currency = false) {
    return algolia.formatMoney(Number(value) * 100, currency);
  };

  const escapeHtml = function escapeHtml(unsafe) {
    return (unsafe || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  };

  const getBoundHtml = () => {
    const boundHtml = html.bind((tag, atts, ...kids) => ({ tag, atts, kids }));
    const addSlash = html =>
      html.replace(
        /<(area|base|br|col|command|embed|hr|img|input|keygen|link|meta|param|source|track|wbr)([^<>]*?)\/?>/gi,
        "<$1$2/>"
      );
    const voidBoundHtml = (strings, ...values) =>
      boundHtml(strings.map(addSlash), ...values);

    return voidBoundHtml;
  };

  algolia.helpers = {
    marketsEnabled: function marketsEnabled() {
      return algolia.config.markets_indexing_enabled &&
        algolia.config.markets_mapping?.length
        ? true
        : false;
    },
    activeCurrencyCode: function activeCurrencyCode() {
      return Shopify.currency.active.toLowerCase();
    },
    formatNumber: function formatNumber(val) {
      return Number(val).toLocaleString();
    },
    localizeHitData: hit => {
      if (algolia.helpers.marketsEnabled()) {
        hit.currency = algolia.config.store_currency.toLowerCase();
      }

      if (
        !algolia.helpers.marketsEnabled() ||
        algolia.helpers.activeCurrencyCode() ===
          algolia.config.store_currency.toLowerCase()
      ) {
        return hit;
      }
      const currencyKey = `market_pricing_${algolia.helpers.activeCurrencyCode()}`;

      if (currencyKey in hit) {
        hit.price = hit[currencyKey]?.price || hit.price;
        hit.compare_at_price =
          hit[currencyKey]?.compare_at_price || hit.compare_at_price;
        hit.variants_min_price =
          hit[currencyKey]?.variants_min_price || hit.variants_min_price;
        hit.variants_max_price =
          hit[currencyKey]?.variants_max_price || hit.variants_max_price;
        hit.variants_compare_at_price_min =
          hit[currencyKey]?.variants_compare_at_price_min ||
          hit.variants_compare_at_price_min;
        hit.variants_compare_at_price_max =
          hit[currencyKey]?.variants_compare_at_price_max ||
          hit.variants_compare_at_price_max;
        hit.price_ratio = hit[currencyKey]?.price_ratio || hit.price_ratio;
        hit.currency = currencyKey.split("_").pop();
      }

      return hit;
    },

    convertPrice: function convertPrice(price) {
      if(Shopify.currency.rate === '1.0') {
        return price;
      }
      return Math.ceil(price * Shopify.currency.rate);
    },

    displayPrice: function displayPrice(item, distinct = null, showCurrency = false) {
      distinct ??= Boolean(algolia.config.show_products);

      if (distinct) {
        let min = item.variants_min_price;
        let max = item.variants_max_price;
        let discountMultiplier = 1;

        if (item.option_names.map(option => option.toLowerCase()).includes('subscription')) {
          discountMultiplier = item.tags.includes('bundle') ? 0.85 : 0.9;
        }

        if (min && max && min !== max) {
          max = this.convertPrice(max) * discountMultiplier / 3;
          return window.variantStrings.fromPrice.replaceAll(
            "[price]",
            formatPrice(max, showCurrency)
          );
        }
      }

      const convertedPrice = this.convertPrice(item.price);
      return formatPrice(convertedPrice, showCurrency);
    },
    displayStrikedPrice: function displayStrikedPrice(
      price,
      compare_at_price,
      currency
    ) {
      const comparing =
        Number(compare_at_price) && Number(compare_at_price) > Number(price);
      if (comparing) {
        return formatPrice(compare_at_price, currency);
      }
    },
    displayDiscount: function displayDiscount(
      price,
      compare_at_price,
      price_ratio
    ) {
      const comparing =
        Number(compare_at_price) && Number(compare_at_price) > Number(price);
      const discount_ratio = 1.0 - price_ratio;
      if (comparing) {
        return `- ${Math.floor(discount_ratio * 100)} %`;
      }
    },
    renderPrice: function price(item, variant, showCurrency = false) {
      const onSale =
        item.compare_at_price && item.compare_at_price > item.price;
      const soldOut = !item.inventory_available;
      const classes = [
        "price",
        soldOut ? "price--sold-out" : null,
        onSale ? "price--on-sale" : null,
        variant ? `price--${variant}` : null,
      ].filter(Boolean);

      return html`
        <div class="${classes.join(" ")}">
          <div>
            <div class="price__regular">
              <span class="visually-hidden visually-hidden--inline">
                Regular price
              </span>
              <span class="price-item price-item--regular price_${item.id}">
                ${algolia.helpers.displayPrice(item, item._distinct, showCurrency)}
              </span>
            </div>
            <div class="price__sale">
              ${onSale
                ? html`
                    <span class="visually-hidden visually-hidden--inline">
                      Regular price
                    </span>
                    <span>
                      <s class="price-item price-item--regular">
                        ${algolia.helpers.displayStrikedPrice(
                          item.price,
                          item.compare_at_price,
                          item.currency
                        )}
                      </s>
                    </span>
                  `
                : ""}
              <span class="visually-hidden visually-hidden--inline">
                Sale price
              </span>
              <span class="price-item price-item--sale price-item--last">
                ${algolia.helpers.displayPrice(item, item._distinct, showCurrency)}
              </span>
            </div>
          </div>
        </div>
      `;
    },
    instantsearchLink: function instantsearchLink(hit, type = "product") {
      if (type == "faq") {
        if (!Shopify.helpCenterEmbedded || !Shopify.helpCenterPath) {
          return hit.url;
        }

        const articlePath = `/en-US/${hit.handle}-${hit.id}`;

        return (
          Shopify.helpCenterPath + "?hcUrl=" + encodeURIComponent(articlePath)
        );
      }

      const getPrefix = (hit, type) => {
        const prefixes = {
          product: "products/",
          page: "pages/",
          article: "blogs/",
        };

        const prefix = prefixes[type];

        return type == "article" ? prefix + hit.blog?.handle + "/" : prefix;
      };

      const prefix = getPrefix(hit, type);

      let url = Shopify.routes.root + prefix + hit.handle;

      if (type === "product") {
        const addVariantId = !hit._distinct && hit.objectID !== hit.id;
        url = url + (addVariantId ? "?variant=" + hit.objectID : "");
      }

      return url;
    },
    fullTitle: function fullTitle(title, distinct, variant_title) {
      let res = title;
      if (
        !distinct &&
        variant_title &&
        variant_title !== "Default Title" &&
        variant_title !== "Default"
      ) {
        res += " (" + variant_title + ")";
      }

      return escapeHtml(res);
    },
    variantTitleAddition: function variantTitleAddition(item, distinct) {
      let res = " ";
      if (
        !distinct &&
        item.variant_title &&
        item.variant_title !== "Default Title" &&
        item.variant_title !== "Default"
      ) {
        res += `( ${item.variant_title} )`;
      }
      return res;
    },
    parseHtml: function (markup, clean = true) {
      const boundHtml = getBoundHtml();
      if (clean) {
        // TODO: Move thise processing to the index fetch script!
        markup = markup
          .replaceAll(
            /<span style="(?=font-size: 12px).*"[^>]*>((?:(?!<\/span>).)*)<\/span>/g,
            "<small>$1</small>"
          )
          .replaceAll(/\s(?:style|id|dir)="[^"]*"/g, "")
          .replaceAll(
            /<(a\s(?:(?!target=)[^>])*href="[^"]*"(?:(?!target=)[^>])*)>/g,
            '<$1 target="_blank" rel="noopener noreferrer">'
          )
          .replaceAll(
            /<p><span>((?:(?!<\/span>).)*)<\/span><\/p>/g,
            "<p>$1</p>"
          )
          .replaceAll("<p><br></p>", "");
      }

      const fragment = document.createRange().createContextualFragment(markup);
      const elements = Array.from(fragment.children).map(el => el.outerHTML);
      return boundHtml(elements);
    },
    // renderTemplate: function (template) {
    //   const boundHtml = getBoundHtml();
    //   return boundHtml(template);
    // },
    hexToRGB: function hexToRGB(hex) {
      if (!hex) return ``;
      if (hex.length == 4) {
        hex = hex.substring(1).replace(/./g, "$&$&");
      }
      const m = hex.match(/^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i);
      return `${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(
        m[3],
        16
      )}`;
    },
    sizedImage: function sizedImage(sizeInput, item, _distinct) {
      const image = _distinct ? item.product_image : item.image;
      if (!image) {
        return "http://cdn.shopify.com/s/images/admin/no-image-compact.gif";
      }
      const size = sizeInput.replace(/^\s+|\s+$/g, ""); // Render and trim
      if (size === "original") {
        return image;
      }
      return image.replace(/\/(.*)\.(\w{2,4})/g, "/$1_" + size + ".$2");
    },
    imageSource: function imageSource(image, width, height = null) {
      let sizeString = `width=${width}`;

      if (height) sizeString += `&height=${height}`;

      const join = image.includes("?") ? "&" : "?";

      return `${image}${join}${sizeString}`;
    },
    imageElement: function imageElement(
      item,
      sizes,
      heightMultiplier = null,
      _distinct = item._distinct
    ) {
      const image = _distinct ? item.product_image : item.image;
      // TODO: Test
      if (!image) {
        return html`<img
          src="http://cdn.shopify.com/s/images/admin/no-image-compact.gif"
        />`;
      }

      const widths = [165, 360, 533, 720, 940, 1066];
      const heights = heightMultiplier
        ? widths.map(w => Math.ceil(w * heightMultiplier))
        : null;

      const srcset = [];
      for (const [i, width] of widths.entries()) {
        const height = heights?.[i] ?? null;
        srcset.push(
          `${algolia.helpers.imageSource(image, width, height)} ${width}w`
        );
      }
      const boundHtml = getBoundHtml();

      return boundHtml`<img
        srcset="${srcset.join(",")}"
        src="${algolia.helpers.sizedImage("medium", item, item._distinct)}"
        sizes="${sizes}"
        alt="${item.title}"
        class="motion-reduce"
        loading="lazy"
      />`;
    },
    index_suffix: (function () {
      const ALGOLIA_MARKET_KEY = "algolia_market";
      let cache_suffix = "";
      let isMemoized = false;

      return async function memorizedIndexSuffix() {
        if (isMemoized) return cache_suffix;

        if (
          algolia.config.markets_indexing_enabled &&
          algolia.config.markets_mapping?.length
        ) {
          // Check if market has changed, if changed, update market
          let shouldCheckMarketIndex = true;
          try {
            const marketStorage = JSON.parse(
              localStorage.getItem(ALGOLIA_MARKET_KEY) || "{}"
            );

            if (
              marketStorage.id == algolia.shopify.market.id &&
              marketStorage.locale ==
                algolia.shopify.market.language.shop_locale.locale
            ) {
              cache_suffix = marketStorage.suffix
                ? `_${marketStorage.suffix}`
                : "";
              shouldCheckMarketIndex = false;
              isMemoized = true;
            }
          } catch (e) {
            localStorage.removeItem(ALGOLIA_MARKET_KEY);
            cache_suffix = "";
            isMemoized = true;
          }

          if (shouldCheckMarketIndex) {
            try {
              // key = {market_id}_{market_language}
              const key = `${algolia.shopify.market.id}_${algolia.shopify.market.language.shop_locale.locale}`;
              const market = algolia.config.markets_mapping.find(
                market => market.key === key
              );
              if (market) {
                const indexSuffix = market.suffix ? `_${market.suffix}` : "";

                // indexName = {prefix}_{type}_{market_title}_{language_code}
                const indexName = `${algolia.config.index_prefix}products${indexSuffix}`;
                const index = algolia.searchClient.initIndex(indexName);

                await index.search("", {
                  page: 1,
                  hitsPerPage: 1,
                  analytics: false,
                });
                cache_suffix = indexSuffix;
                localStorage.setItem(
                  ALGOLIA_MARKET_KEY,
                  JSON.stringify(market)
                );
                isMemoized = true;
              }
            } catch (e) {
              localStorage.removeItem(ALGOLIA_MARKET_KEY);
              cache_suffix = "";
              isMemoized = true;
            }
          }
          return cache_suffix;
        } else {
          return cache_suffix;
        }
      };
    })(),
  };

  algolia.config.index_suffix = algolia.helpers.index_suffix();

  [
    "pico",
    "icon",
    "thumb",
    "small",
    "compact",
    "medium",
    "large",
    "grande",
    "original",
  ].forEach(function (size) {
    algolia.helpers[size + "Image"] = function (item) {
      const image = item.distinct ? item.product_image : item.image;

      if (!image) {
        return "http://cdn.shopify.com/s/images/admin/no-image-compact.gif";
      }

      if (size === "original") {
        return image;
      }

      return image.replace(/\/(.*)\.(\w{2,4})/g, "/$1_" + size + ".$2");
    };
  });
})(window.algoliaShopify);
